import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { API_ROUTES } from '../ApiRoutes';
import instance, { GET, PUT } from '../services/api';
import {
  CompaniesOverviewModel,
  CompaniesPagination,
  CompanyBranch,
  CompanyBranchResponse,
  CompanyCreate,
  CompanyInfo,
  CompanyInfoRoot,
  CompanyRegistrationLink,
  UpdateEscalationRequest,
} from '../models/company.model';
import { SignedPutUrl } from '../models/signed-put-rl.model';
import { IListRequest } from '@data/models/common.model';
import { PurchasedEventTypes } from 'store/companySlide';
import { buildApiPath } from 'utils';
import { UpdateCompanyAiContextRequest } from '../models/company-ai-context.model';

export const getCompaniesOverview =
  async (): Promise<CompaniesOverviewModel> => {
    const data: AxiosResponse<CompaniesOverviewModel> = await instance
      .get(API_ROUTES.companiesOverview)
      .catch((error) => {
        throw error;
      });

    return data.data;
  };

export const getCompaniesPagination = async (
  page: number,
  searchQuery?: string,
  isParentCompany?: boolean,
): Promise<CompaniesPagination> => {
  const data: AxiosResponse<CompaniesPagination> = await instance
    .get(
      API_ROUTES.companies +
        `?page=${page}&searchQuery=${searchQuery || ''}&isParentCompany=${
          isParentCompany || false
        }`,
    )
    .catch((error) => {
      throw error;
    });

  return data.data;
};

export const getCompaniesPaginationBranchLocation = async (
  page: number,
  searchQuery?: string,
  isParentCompany?: boolean,
  companyId?: string,
): Promise<CompaniesPagination> => {
  const data: AxiosResponse<CompaniesPagination> = await instance
    .get(
      API_ROUTES.companiesBranchLocation +
        `?page=${page}&searchQuery=${searchQuery || ''}&isParentCompany=${
          isParentCompany || false
        }&companyId=${companyId || ''}`,
    )
    .catch((error) => {
      throw error;
    });

  return data.data;
};

export const createCompany = async (
  payload: CompanyCreate,
): Promise<CompanyInfo> => {
  const data: AxiosResponse<CompanyInfoRoot> = await instance
    .post(API_ROUTES.companies, payload)
    .catch((error) => {
      throw error;
    });

  return data.data?.company;
};

export const getCompanyDetail = async (
  companyId: string,
): Promise<{ company: CompanyInfo }> => {
  const data: AxiosResponse<{ company: CompanyInfo }> = await instance
    .get(API_ROUTES.company(companyId))
    .catch((error) => {
      throw error;
    });

  return data.data;
};

export const getCompanyRegistrationLinks = async (
  companyId: string,
): Promise<CompanyRegistrationLink[]> => {
  const data: AxiosResponse<{ registrationLinks: CompanyRegistrationLink[] }> =
    await instance.get(API_ROUTES.companyLinks(companyId)).catch((error) => {
      throw error;
    });

  return data.data.registrationLinks;
};

export const getCompaniesSearch = async (
  query: string,
): Promise<{ companyId: string; name: string }[]> => {
  const data: AxiosResponse<{
    companies: { companyId: string; name: string }[];
  }> = await instance
    .get(API_ROUTES.companiesSearch + `?query=${query}`)
    .catch((error) => {
      throw error;
    });
  return data.data.companies;
};

export const updateCompany = async (
  companyId: string,
  payload: CompanyInfo,
): Promise<CompanyInfo> => {
  const data: AxiosResponse<CompanyInfo> = await instance
    .patch(API_ROUTES.company(companyId), payload)
    .catch((error) => {
      throw error;
    });

  return data.data;
};

export const getSignedPutUrl = async (
  companyId: string,
  contentType: string,
  isActive: boolean,
): Promise<SignedPutUrl> => {
  const data: AxiosResponse<SignedPutUrl> = await instance
    .get(
      API_ROUTES.company(companyId) +
        `/logo?contentType=${contentType}&isActive=${isActive}`,
    )
    .catch((error) => {
      throw error;
    });
  return data.data;
};

export const uploadLogoCompanyToAWS = async (
  signedPutUrl: string,
  file: File,
) => {
  const config: AxiosRequestConfig = {
    method: 'put',
    url: signedPutUrl,
    headers: {
      'Content-Type': file.type,
    },
    data: file,
  };
  const data = await axios(config);
  return data.data;
};

export const deleteCompany = async (companyId: string) => {
  const data: AxiosResponse<any> = await instance
    .delete(API_ROUTES.deleteCompany(companyId))
    .catch((error) => {
      throw error;
    });

  return data.data;
};

export const getCompanyLinkNonEmployeeSponsoredAccount = async (
  companyId: string,
): Promise<CompanyRegistrationLink[]> => {
  const data: AxiosResponse<{ registrationLinks: CompanyRegistrationLink[] }> =
    await instance
      .get(API_ROUTES.companyLinkNonEmployeeSponsoredAccount(companyId))
      .catch((error) => {
        throw error;
      });

  return data.data.registrationLinks;
};

export const getAllCompanies = async (): Promise<CompaniesPagination> => {
  const data: AxiosResponse<CompaniesPagination> = await instance
    .get(API_ROUTES.allCompanies)
    .catch((error) => {
      throw error;
    });

  return data.data;
};

export const getEscalationUsers = async (companyId: string) => {
  try {
    const data: AxiosResponse<any> = await GET(
      API_ROUTES.getEscalationUsers(companyId),
    );
    return data.data;
  } catch (e) {
    if (typeof e === 'string') {
      throw Error(e.toUpperCase());
    } else if (e instanceof Error) {
      throw Error(e.message);
    }
    throw Error('');
  }
};

export const getEscalationCompany = async (companyId: string) => {
  try {
    const data: AxiosResponse<any> = await GET(
      API_ROUTES.getEscalationCompany(companyId),
    );
    return data.data;
  } catch (e) {
    if (typeof e === 'string') {
      throw Error(e.toUpperCase());
    } else if (e instanceof Error) {
      throw Error(e.message);
    }
    throw Error('');
  }
};

export const updateEscalationCompany = async (
  companyId: string,
  request: UpdateEscalationRequest,
) => {
  try {
    const data: AxiosResponse<any> = await PUT(
      API_ROUTES.updateEscalationCompany(companyId),
      request,
    );
    return data.data;
  } catch (e) {
    if (typeof e === 'string') {
      throw Error(e.toUpperCase());
    } else if (e instanceof Error) {
      throw Error(e.message);
    }
    throw Error('');
  }
};

export const getCompanyAiContext = async (companyId: string) => {
  try {
    const data: AxiosResponse<any> = await GET(
      API_ROUTES.getCompanyAiContext(companyId),
    );
    return data.data;
  } catch (e) {
    if (typeof e === 'string') {
      throw Error(e.toUpperCase());
    } else if (e instanceof Error) {
      throw Error(e.message);
    }
    throw Error('');
  }
};

export const updateCompanyAiContext = async (
  companyId: string,
  request: UpdateCompanyAiContextRequest,
) => {
  try {
    const data: AxiosResponse<any> = await PUT(
      API_ROUTES.updateCompanyAiContext(companyId),
      request,
    );
    return data.data;
  } catch (e) {
    if (typeof e === 'string') {
      throw Error(e.toUpperCase());
    } else if (e instanceof Error) {
      throw Error(e.message);
    }
    throw Error('');
  }
};

export const getPurchasedEventTypes = async (companyId: string) => {
  try {
    const data: AxiosResponse<any> = await GET(
      API_ROUTES.getPurchasedEventTypes(),
    );
    return data.data;
  } catch (e) {
    if (typeof e === 'string') {
      throw Error(e.toUpperCase());
    } else if (e instanceof Error) {
      throw Error(e.message);
    }
    throw Error('');
  }
};

export const getBranchCompanies = async (
  companyId: string,
  request: IListRequest,
): Promise<CompanyBranchResponse> => {
  const data: AxiosResponse<CompanyBranchResponse> = await instance
    .get(
      API_ROUTES.companyBranch(companyId) +
        `?page=${request.page}&sortBy=${request.sortBy}&order=${request.order}`,
    )
    .catch((error) => {
      throw error;
    });

  return data.data;
};

export const getAllBranchCompaniesOverview = async (
  companyId: string,
): Promise<CompanyBranch[]> => {
  const data: AxiosResponse<CompanyBranchResponse> = await instance
    .get(API_ROUTES.companyAllBranch(companyId))
    .catch((error) => {
      throw error;
    });

  return data.data.companies;
};

export interface CompanyEventTypeOptions {
  events: PurchasedEventTypes[];
  noEventMemberCount?: number;
}

export const getAllEvents = async (
  companyId: string,
  branchCompanies?: string[],
): Promise<CompanyEventTypeOptions> => {
  const data: AxiosResponse<CompanyEventTypeOptions> = await instance
    .get(
      buildApiPath({
        path: API_ROUTES.companyEvents(companyId),
        params: {
          branchCompanies:
            branchCompanies && branchCompanies.length > 0
              ? branchCompanies
              : undefined,
        },
      }),
    )
    .catch((error) => {
      throw error;
    });

  return data.data;
};
